import { supabase } from '../lib/supabase';
import {
  authCardHtml, mountAuthScreen, fieldHtml, alertHtml, submitHtml, noticeHtml,
  bindReveal, bindCaps, capsHintHtml, bindMarkLight, showAlert, clearAlert, clearAlertOnInput, setBusy, setDone, markState, authErrorCopy,
} from './auth/AuthShell';

export type SetPasswordMode = 'invite' | 'recovery';

// R116 #1: the "Welcome home" shell (pages/auth) for both a first-time invite
// and a password reset — same ids, same Supabase call. The account's email
// sits in a read-only autocomplete=username field so a password manager files
// the new password under the right login (Skeptic D6).
export class UpdatePasswordPage {
  private container: HTMLElement;
  private onSuccess: () => void;
  private mode: SetPasswordMode;
  private revertReveal: () => void = () => {};

  constructor(container: HTMLElement, onSuccess: () => void, mode: SetPasswordMode = 'recovery') {
    this.container = container;
    this.onSuccess = onSuccess;
    this.mode = mode;
  }

  async render(): Promise<void> {
    const invite = this.mode === 'invite';
    const card = mountAuthScreen(this.container, authCardHtml({
      screen: 'set-password',
      hello: invite ? '<b>Welcome aboard.</b> Set a password to finish.' : '<b>Choose a new password</b>',
      helper: invite ? 'You’ll sign in with your email and this password.' : 'Pick one you don’t use anywhere else.',
      body: `
        ${alertHtml('update-error')}
        <div id="update-success" hidden></div>
        <form id="update-form" novalidate>
          <div class="au-account" id="update-account" hidden>
            <span class="au-account-l">Account</span>
            <input class="au-account-in" id="update-username" type="email" name="username" autocomplete="username" readonly tabindex="-1" aria-label="Account email">
          </div>
          ${fieldHtml({ id: 'new-password', label: 'New password', type: 'password', name: 'new-password', autocomplete: 'new-password', placeholder: 'At least 6 characters', reveal: true, after: capsHintHtml('update-caps') })}
          ${fieldHtml({ id: 'confirm-password', label: 'Confirm password', type: 'password', name: 'confirm-password', autocomplete: 'new-password', placeholder: 'Type it again', after: '<div class="au-hint" id="update-match" aria-live="polite"></div>' })}
          ${submitHtml('update-submit', invite ? 'Set password' : 'Update password')}
        </form>`,
    }));
    const pw = document.getElementById('new-password') as HTMLInputElement | null;
    const confirm = document.getElementById('confirm-password') as HTMLInputElement | null;
    pw?.focus();
    this.revertReveal = bindReveal(card, ['new-password', 'confirm-password']);
    bindCaps(pw, document.getElementById('update-caps'));
    bindMarkLight(card, [pw, confirm]);
    clearAlertOnInput(document.getElementById('update-error'), [pw, confirm]);
    const match = document.getElementById('update-match');
    const updMatch = () => {
      if (!match || !pw || !confirm) return;
      const ok = !!confirm.value && confirm.value === pw.value;
      match.textContent = !confirm.value ? '' : ok ? '✓ Passwords match' : (pw.value.startsWith(confirm.value) ? '' : "Doesn't match yet");
      match.classList.toggle('ok', ok);
    };
    pw?.addEventListener('input', updMatch);
    confirm?.addEventListener('input', updMatch);
    this.bindEvents();

    // Fill the account line once the session answers (the form is usable before that).
    try {
      const { data } = await supabase.auth.getSession();
      const em = data?.session?.user?.email || '';
      const acct = document.getElementById('update-account');
      const user = document.getElementById('update-username') as HTMLInputElement | null;
      if (em && user && acct) { user.value = em; acct.hidden = false; }
    } catch { /* the field just stays hidden */ }
  }

  private bindEvents(): void {
    const form = document.getElementById('update-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleUpdate();
      });
    }
  }

  private async handleUpdate(): Promise<void> {
    const pwEl = document.getElementById('new-password') as HTMLInputElement;
    const confirmEl = document.getElementById('confirm-password') as HTMLInputElement;
    const newPassword = pwEl.value;
    const confirmPassword = confirmEl.value;
    const errorDiv = document.getElementById('update-error');
    const successDiv = document.getElementById('update-success');
    const submitBtn = document.getElementById('update-submit') as HTMLButtonElement;
    const card = this.container.querySelector('.au-card') as HTMLElement | null;
    const label = this.mode === 'invite' ? 'Set password' : 'Update password';
    if (submitBtn?.disabled) return;
    this.revertReveal();

    if (newPassword !== confirmPassword) {
      showAlert(errorDiv, "The two passwords don't match.", [confirmEl]);
      confirmEl.focus();
      return;
    }

    if (newPassword.length < 6) {
      showAlert(errorDiv, 'Use at least 6 characters.', [pwEl]);
      pwEl.focus();
      return;
    }

    clearAlert(errorDiv, [pwEl, confirmEl]);
    setBusy(submitBtn, true, 'Saving…');
    markState(card, { busy: true });

    let error: any = null;
    try {
      ({ error } = await supabase.auth.updateUser({
        password: newPassword
      }));
    } catch (e) { error = e; }
    markState(card, { busy: false });

    if (error) {
      showAlert(errorDiv, authErrorCopy(error, 'update'));
      setBusy(submitBtn, false, label);
    } else {
      if (successDiv) {
        successDiv.outerHTML = noticeHtml({ tone: 'ok', id: 'update-success', text: 'Password saved. Taking you to sign in…' });
      }
      clearAlert(errorDiv);
      markState(card, { lit: true, open: true });
      setDone(submitBtn, 'Password saved');

      setTimeout(() => {
        this.onSuccess();
      }, 2000);
    }
  }
}
